<template>
  <v-dialog v-model="dialog" max-width="1200" persistent scrollable>
    <v-card>
      <!-- ========================================================= -->
      <!-- HEADER -->
      <!-- ========================================================= -->

      <v-card-title class="grey lighten-3">
        <strong>
          {{ $t("_site_parameters_") }}
        </strong>

        <span class="ml-6">
          <strong>{{ installation.installation_name }}</strong>
        </span>

        <span class="ml-6">
          <strong>{{ installation.installed_power_kwc }} kWc</strong>
        </span>

        <v-spacer />

        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text style="max-height: 75vh; overflow-y: auto">
        <!-- ========================================================= -->
        <!-- GENERAL PARAMETERS -->
        <!-- ========================================================= -->
        <v-form ref="parameterForm">
          <v-card outlined class="parameter-card mb-4 pa-4">
            <div class="section-title">
              {{ $t("_general_parameters_") }}
            </div>

            <!-- Subscribed apparent power -->
            <v-row dense align="center">
              <v-col cols="4">
                {{ $t("_subscribed_apparent_power_kVA_") }}
              </v-col>

              <v-col cols="2">
                <v-select
                  v-if="hasIncrement('subscribed_power')"
                  dense
                  outlined
                  hide-details="auto"
                  :items="getLimitOptions('subscribed_power')"
                  v-model="form.subscribed_power"
                />

                <v-text-field
                  v-else
                  dense
                  outlined
                  hide-details="auto"
                  type="number"
                  v-model.number="form.subscribed_power"
                  :min="getMin('subscribed_power')"
                  :max="getMax('subscribed_power')"
                  :step="getStep('subscribed_power')"
                  :rules="getRules('subscribed_power')"
                />
              </v-col>

              <v-col cols="6">
                <span
                  v-if="defaultParams.subscribed_power !== undefined"
                  class="default-value"
                >
                  {{ $t("_default_") }}:
                  {{ defaultParams.subscribed_power }}
                </span>
              </v-col>
            </v-row>

            <!-- PR -->
            <v-row dense align="center">
              <v-col cols="4">
                {{ $t("_PR_compliance_threshold_") }}
              </v-col>

              <v-col cols="2">
                <v-text-field
                  dense
                  outlined
                  hide-details="auto"
                  type="number"
                  suffix="%"
                  v-model.number="form.pr_threshold"
                  :min="getMin('pr_threshold')"
                  :max="getMax('pr_threshold')"
                  :step="getStep('pr_threshold')"
                  :rules="getRules('pr_threshold')"
                />
              </v-col>

              <v-col cols="6">
                <span
                  v-if="defaultParams.pr_threshold !== undefined"
                  class="default-value"
                >
                  {{ $t("_default_") }}: {{ defaultParams.pr_threshold }} %
                </span>
              </v-col>
            </v-row>

            <!-- Availability -->
            <v-row dense align="center">
              <v-col cols="4">
                {{ $t("_availability_compliance_threshold_") }}
              </v-col>

              <v-col cols="2">
                <v-text-field
                  dense
                  outlined
                  hide-details="auto"
                  type="number"
                  suffix="%"
                  v-model.number="form.availability_threshold"
                  :min="getMin('availability_threshold')"
                  :max="getMax('availability_threshold')"
                  :step="getStep('availability_threshold')"
                  :rules="getRules('availability_threshold')"
                />
              </v-col>

              <v-col cols="6">
                <span
                  v-if="defaultParams.availability_threshold !== undefined"
                  class="default-value"
                >
                  {{ $t("_default_") }}:
                  {{ defaultParams.availability_threshold }} %
                </span>
              </v-col>
            </v-row>

            <!-- Electricity price -->
            <v-row dense align="center">
              <v-col cols="4">
                {{ $t("_electricity_tariff_average_") }}
              </v-col>

              <v-col cols="2">
                <v-text-field
                  dense
                  outlined
                  hide-details="auto"
                  type="number"
                  suffix="€/MWh"
                  v-model.number="form.electricity_price"
                  :min="getMin('electricity_price')"
                  :max="getMax('electricity_price')"
                  :step="getStep('electricity_price')"
                  :rules="getRules('electricity_price')"
                />
              </v-col>

              <v-col cols="6">
                <span
                  v-if="defaultParams.electricity_price !== undefined"
                  class="default-value"
                >
                  {{ $t("_default_") }}:
                  {{ defaultParams.electricity_price }} €/MWh
                </span>
              </v-col>
            </v-row>
            <!-- ===================================================== -->
            <!-- CURTAILMENT / NEGATIVE PRICE / OUTAGE -->
            <!-- ===================================================== -->

            <div class="monthly-section mt-5">
              <div class="subsection-title">
                {{ $t("_curtailment_negative_price_outage_") }}
              </div>

              <div class="monthly-header">
                <div class="row-title"></div>

                <div v-for="month in months" :key="month" class="month-label">
                  {{ $t(month) }}
                </div>
              </div>

              <!-- ESTIMATED -->
              <div class="monthly-row">
                <span class="row-title">
                  {{ $t("_estimated_") }}
                </span>
                <v-text-field
                  v-for="(value, index) in getDefaultMonthlyValues(
                    'curtailment_estimated'
                  )"
                  :key="'curtailment-estimated-' + index"
                  dense
                  outlined
                  hide-details="auto"
                  disabled
                  readonly
                  suffix="%"
                  :value="value"
                  class="small-field"
                />
              </div>

              <!-- RETAINED -->
              <div class="monthly-row">
                <span class="row-title">
                  {{ $t("_retained_") }}
                </span>

                <v-text-field
                  v-for="(value, index) in form.curtailment_retained"
                  :key="'curtailment-retained-' + index"
                  dense
                  outlined
                  type="number"
                  suffix="%"
                  v-model.number="form.curtailment_retained[index]"
                  :min="getMin('curtailment_retained')"
                  :max="getMax('curtailment_retained')"
                  :step="getStep('curtailment_retained')"
                  :rules="getRules('curtailment_retained')"
                  class="small-field"
                />
              </div>
            </div>

            <!-- ===================================================== -->
            <!-- TEMPORARY INJECTED POWER -->
            <!-- ===================================================== -->

            <v-row dense class="mt-2">
              <v-col cols="4">
                <strong>
                  {{ $t("_temporary_power_limitation_") }}
                </strong>
              </v-col>
            </v-row>

            <div
              v-for="(item, index) in form.temporary_injected_power"
              :key="item.id"
              class="temporary-power-row"
            >
              <v-text-field
                dense
                outlined
                type="number"
                label="kVA"
                v-model.number="item.power"
                :min="getMin('temporary_injected_power_value')"
                :max="getMax('temporary_injected_power_value')"
                :step="getStep('temporary_injected_power_value')"
                :rules="getRules('temporary_injected_power_value')"
                class="temporary-power-field"
              />

              <span class="mx-2">
                {{ $t("_from_") }}
              </span>

              <v-menu
                v-model="item.startMenu"
                :close-on-content-click="false"
                offset-y
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    dense
                    outlined
                    hide-details="auto"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :value="formatDate(item.start)"
                    class="temporary-date-field"
                  />
                </template>

                <v-date-picker
                  v-model="item.start"
                  @input="item.startMenu = false"
                />
              </v-menu>

              <span class="mx-2">
                {{ $t("_to_") }}
              </span>

              <v-menu
                v-model="item.endMenu"
                :close-on-content-click="false"
                offset-y
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    dense
                    outlined
                    hide-details="auto"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :value="formatDate(item.end)"
                    class="temporary-date-field"
                  />
                </template>

                <v-date-picker
                  v-model="item.end"
                  @input="item.endMenu = false"
                />
              </v-menu>

              <v-btn
                icon
                small
                color="error"
                class="ml-2"
                @click="removeTemporaryPower(index)"
              >
                <v-icon small> mdi-delete </v-icon>
              </v-btn>
            </div>

            <v-btn small text color="primary" @click="addTemporaryPower">
              <v-icon left small> mdi-plus </v-icon>

              {{ $t("_add_") }}
            </v-btn>
          </v-card>
          <!-- ========================================================= -->
          <!-- CLEANING -->
          <!-- ========================================================= -->

          <v-card outlined class="parameter-card mb-4 pa-4">
            <div class="section-title">
              {{ $t("_nettoyage_parameters_") }}
            </div>

            <!-- Soiling threshold -->
            <v-row dense align="center">
              <v-col cols="4">
                {{ $t("_soiling_losses_threshold_") }}
              </v-col>

              <v-col cols="2">
                <v-text-field
                  dense
                  outlined
                  hide-details="auto"
                  type="number"
                  suffix="%"
                  v-model.number="form.cleaning_soiling_threshold"
                  :min="getMin('cleaning_soiling_threshold')"
                  :max="getMax('cleaning_soiling_threshold')"
                  :step="getStep('cleaning_soiling_threshold')"
                  :rules="getRules('cleaning_soiling_threshold')"
                />
              </v-col>

              <v-col cols="6">
                <span
                  v-if="defaultParams.cleaning_soiling_threshold !== undefined"
                  class="default-value"
                >
                  {{ $t("_default_") }}:
                  {{ defaultParams.cleaning_soiling_threshold }} %
                </span>
              </v-col>
            </v-row>

            <!-- Cleaning cost -->
            <v-row dense align="center">
              <v-col cols="4">
                {{ $t("_cleaning_costs_") }}
              </v-col>

              <v-col cols="2">
                <v-text-field
                  dense
                  outlined
                  hide-details="auto"
                  type="number"
                  suffix="€"
                  v-model.number="form.cleaning_cost"
                  :min="getMin('cleaning_cost')"
                  :max="getMax('cleaning_cost')"
                  :step="getStep('cleaning_cost')"
                  :rules="getRules('cleaning_cost')"
                />
              </v-col>

              <v-col cols="6">
                <span
                  v-if="defaultParams.cleaning_cost !== undefined"
                  class="default-value"
                >
                  {{ $t("_default_") }}: {{ defaultParams.cleaning_cost }} €
                </span>
              </v-col>
            </v-row>

            <!-- ========================================================= -->
            <!-- CLEANING DATES -->
            <!-- ========================================================= -->

            <v-row class="mt-4">
              <v-col cols="4">
                <div class="date-title">
                  {{ $t("_cleaning_dates_") }}
                </div>

                <v-switch
                  dense
                  hide-details="auto"
                  inset
                  v-model="form.cleaning_auto_detection"
                  :label="$t('_cleaning_auto_detection_')"
                  class="mt-2"
                />
              </v-col>

              <v-col cols="8">
                <!-- DEFAULT DATES -->
                <div class="date-box-wrapper mb-3">
                  <div class="date-box-label">
                    {{ $t("_default_cleaning_dates_") }}
                  </div>

                  <v-textarea
                    dense
                    outlined
                    readonly
                    hide-details="auto"
                    rows="2"
                    :value="formatDates(defaultParams.cleaning_dates)"
                    class="default-date-box"
                  />
                </div>

                <!-- CLIENT DATES -->
                <div class="date-box-wrapper">
                  <div class="date-box-label">
                    {{ $t("_cleaning_dates_filled_by_client_") }}
                  </div>

                  <v-combobox
                    v-model="form.cleaning_dates"
                    multiple
                    dense
                    outlined
                    hide-details="auto"
                    readonly
                    class="client-date-box"
                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip
                        small
                        close
                        @click:close="removeCleaningDate(index)"
                      >
                        {{ formatDate(item) }}
                      </v-chip>
                    </template>

                    <template v-slot:append>
                      <v-menu
                        v-model="cleaningDateMenu"
                        :close-on-content-click="false"
                        offset-y
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon small v-bind="attrs" v-on="on">
                            <v-icon small> mdi-calendar-plus </v-icon>
                          </v-btn>
                        </template>

                        <v-date-picker @input="addCleaningDateFromPicker" />
                      </v-menu>
                    </template>
                  </v-combobox>
                </div>
              </v-col>
            </v-row>

            <!-- Cleaning strategy -->
            <v-row dense align="center" class="mt-3">
              <v-col cols="4">
                {{ $t("_site_cleaning_freq_initial_short_") }}
              </v-col>

              <v-col cols="3">
                <v-select
                  dense
                  outlined
                  hide-details="auto"
                  :items="frequencyOptions"
                  v-model="form.cleaning_freq_initial"
                />
              </v-col>

              <v-col cols="5">
                <span
                  v-if="defaultParams.cleaning_freq_initial !== undefined"
                  class="default-value"
                >
                  {{ $t("_default_") }}:
                  {{ defaultParams.cleaning_freq_initial }}
                </span>
              </v-col>
            </v-row>

            <v-row dense align="center">
              <v-col cols="4">
                {{ $t("_site_cleaning_freq_chosen_short_") }}
              </v-col>

              <v-col cols="3">
                <v-select
                  dense
                  outlined
                  hide-details="auto"
                  :items="strategyOptions"
                  v-model="form.cleaning_freq_chosen"
                />
              </v-col>

              <v-col cols="5">
                <span
                  v-if="defaultParams.cleaning_freq_chosen !== undefined"
                  class="default-value"
                >
                  {{ $t("_default_") }}:
                  {{ defaultParams.cleaning_freq_chosen }}
                </span>
              </v-col>
            </v-row>

            <!-- ===================================================== -->
            <!-- SOILING DYNAMICS -->
            <!-- ===================================================== -->

            <div class="monthly-section mt-5">
              <div class="subsection-title">
                {{ $t("_soiling_dynamics_text_") }}
              </div>

              <div class="monthly-header">
                <div class="row-title"></div>

                <div v-for="month in months" :key="month" class="month-label">
                  {{ $t(month) }}
                </div>
              </div>

              <!-- ESTIMEE -->
              <div class="monthly-row">
                <span class="row-title">
                  {{ $t("_estimated_") }}
                </span>

                <v-text-field
                  v-for="(value, index) in getDefaultMonthlyValues(
                    'cleaning_soiling_estimated'
                  )"
                  :key="'cleaning-estimated-' + index"
                  dense
                  outlined
                  hide-details="auto"
                  disabled
                  readonly
                  suffix="%"
                  :value="value"
                  class="small-field"
                />
              </div>

              <!-- RETENUE -->
              <div class="monthly-row">
                <span class="row-title">
                  {{ $t("_retained_") }}
                </span>

                <v-text-field
                  v-for="(value, index) in form.cleaning_soiling_retained"
                  :key="'cleaning-retained-' + index"
                  dense
                  outlined
                  type="number"
                  suffix="%"
                  v-model.number="form.cleaning_soiling_retained[index]"
                  :min="getMin('cleaning_soiling_retained')"
                  :max="getMax('cleaning_soiling_retained')"
                  :step="getStep('cleaning_soiling_retained')"
                  :rules="getRules('cleaning_soiling_retained')"
                  class="small-field"
                />
              </div>
            </div>
          </v-card>

          <!-- ========================================================= -->
          <!-- PRUNING -->
          <!-- ========================================================= -->

          <v-card outlined class="parameter-card pa-4">
            <div class="section-title">
              {{ $t("_elagage_parameters_") }}
            </div>

            <!-- Shading threshold -->
            <v-row dense align="center">
              <v-col cols="4">
                {{ $t("_shading_losses_threshold_") }}
              </v-col>

              <v-col cols="2">
                <v-text-field
                  dense
                  outlined
                  hide-details="auto"
                  type="number"
                  suffix="%"
                  v-model.number="form.pruning_shading_threshold"
                  :min="getMin('pruning_shading_threshold')"
                  :max="getMax('pruning_shading_threshold')"
                  :step="getStep('pruning_shading_threshold')"
                  :rules="getRules('pruning_shading_threshold')"
                />
              </v-col>

              <v-col cols="6">
                <span
                  v-if="defaultParams.pruning_shading_threshold !== undefined"
                  class="default-value"
                >
                  {{ $t("_default_") }}:
                  {{ defaultParams.pruning_shading_threshold }} %
                </span>
              </v-col>
            </v-row>

            <!-- Pruning cost -->
            <v-row dense align="center">
              <v-col cols="4">
                {{ $t("_pruning_costs_") }}
              </v-col>

              <v-col cols="2">
                <v-text-field
                  dense
                  outlined
                  hide-details="auto"
                  type="number"
                  suffix="€"
                  v-model.number="form.pruning_cost"
                  :min="getMin('pruning_cost')"
                  :max="getMax('pruning_cost')"
                  :step="getStep('pruning_cost')"
                  :rules="getRules('pruning_cost')"
                />
              </v-col>

              <v-col cols="6">
                <span
                  v-if="defaultParams.pruning_cost !== undefined"
                  class="default-value"
                >
                  {{ $t("_default_") }}: {{ defaultParams.pruning_cost }} €
                </span>
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="4">
                <div class="date-title">
                  {{ $t("_pruning_dates_") }}
                </div>

                <v-switch
                  dense
                  hide-details="auto"
                  inset
                  v-model="form.pruning_auto_detection"
                  :label="$t('_pruning_auto_detection_')"
                  class="mt-2"
                />
              </v-col>

              <v-col cols="8">
                <!-- DEFAULT -->
                <div class="date-box-wrapper mb-3">
                  <div class="date-box-label">
                    {{ $t("_default_pruning_dates_") }}
                  </div>

                  <v-textarea
                    dense
                    outlined
                    readonly
                    hide-details="auto"
                    rows="2"
                    :value="formatDates(defaultParams.pruning_dates)"
                    class="default-date-box"
                  />
                </div>

                <!-- CLIENT -->
                <div class="date-box-wrapper">
                  <div class="date-box-label">
                    {{ $t("_pruning_dates_filled_by_client_") }}
                  </div>

                  <v-combobox
                    v-model="form.pruning_dates"
                    multiple
                    chips
                    dense
                    outlined
                    hide-details="auto"
                    readonly
                    deletable-chips
                    class="client-date-box"
                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip
                        small
                        close
                        @click:close="removePruningDate(index)"
                      >
                        {{ formatDate(item) }}
                      </v-chip>
                    </template>
                    <template v-slot:append>
                      <v-menu
                        v-model="pruningDateMenu"
                        :close-on-content-click="false"
                        offset-y
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon small v-bind="attrs" v-on="on">
                            <v-icon small> mdi-calendar-plus </v-icon>
                          </v-btn>
                        </template>

                        <v-date-picker @input="addPruningDateFromPicker" />
                      </v-menu>
                    </template>
                  </v-combobox>
                </div>
              </v-col>
            </v-row>

            <!-- Strategy -->
            <v-row dense align="center" class="mt-3">
              <v-col cols="4">
                {{ $t("_site_pruning_freq_initial_short_") }}
              </v-col>

              <v-col cols="3">
                <v-select
                  dense
                  outlined
                  hide-details="auto"
                  :items="frequencyOptions"
                  v-model="form.pruning_freq_initial"
                />
              </v-col>

              <v-col cols="5">
                <span
                  v-if="defaultParams.pruning_freq_initial !== undefined"
                  class="default-value"
                >
                  {{ $t("_default_") }}:
                  {{ defaultParams.pruning_freq_initial }}
                </span>
              </v-col>
            </v-row>

            <v-row dense align="center">
              <v-col cols="4">
                {{ $t("_site_pruning_freq_chosen_short_") }}
              </v-col>

              <v-col cols="3">
                <v-select
                  dense
                  outlined
                  hide-details="auto"
                  :items="strategyOptions"
                  v-model="form.pruning_freq_chosen"
                />
              </v-col>

              <v-col cols="5">
                <span
                  v-if="defaultParams.pruning_freq_chosen !== undefined"
                  class="default-value"
                >
                  {{ $t("_default_") }}:
                  {{ defaultParams.pruning_freq_chosen }}
                </span>
              </v-col>
            </v-row>

            <!-- Annual evolution -->
            <div class="monthly-section mt-5">
              <div class="subsection-title">
                {{ $t("_shading_dynamics_text_") }}
              </div>

              <div class="monthly-row">
                <span class="row-title">
                  {{ $t("_estimated_") }}
                </span>

                <v-text-field
                  dense
                  outlined
                  hide-details="auto"
                  disabled
                  readonly
                  suffix="%"
                  :value="getDefaultMonthlyValue('pruning_shading_estimated')"
                  class="small-field-pruning"
                />
              </div>

              <div class="monthly-row">
                <span class="row-title">
                  {{ $t("_retained_") }}
                </span>

                <v-text-field
                  dense
                  outlined
                  hide-details="auto"
                  suffix="%"
                  v-model.number="form.pruning_shading_retained"
                  :min="getMin('pruning_shading_retained')"
                  :max="getMax('pruning_shading_retained')"
                  :step="getStep('pruning_shading_retained')"
                  :rules="getRules('pruning_shading_retained')"
                  class="small-field-pruning"
                />
              </div>
            </div>
          </v-card>
        </v-form>
      </v-card-text>

      <!-- ========================================================= -->
      <!-- ACTIONS -->
      <!-- ========================================================= -->

      <v-card-actions>
        <v-spacer />

        <v-btn text @click="close">
          {{ $t("_quitter_sans_enregistrer_") }}
        </v-btn>

        <v-btn color="primary" :loading="saving" @click="save">
          {{ $t("_enregistrer_et_quitter_") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api/api";
import { mapGetters } from "vuex";

export default {
  name: "HelioCleanParameters",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      installation: {},
      defaultParams: {},
      clientParams: {},
      limits: {},
      cleaningDateMenu: false,
      pruningDateMenu: false,

      form: {},

      saving: false,
      loading: false,

      dateMenus: {
        cleaning: {},
        pruning: {},
      },

      frequencyOptions: ["no action","6M", "12M", "24M", "36M", "48M", "60M"],

      strategyOptions: ["_optimal_", "no action","6M", "12M", "24M", "36M", "48M", "60M"],

      months: [
        "_january_",
        "_february_",
        "_march_",
        "_april_",
        "_may_",
        "_june_",
        "_july_",
        "_august_",
        "_september_",
        "_october_",
        "_november_",
        "_december_",
      ],
    };
  },

  computed: {
    ...mapGetters(["role", "installations", "cur_install_id", "constants"]),
    installationId() {
      return this.cur_install_id;
    },
    dialog: {
      get() {
        return this.value;
      },

      set(value) {
        this.$emit("input", value);
      },
    },
  },
  watch: {
    cur_install_id: {
      async handler(newId) {
        this.installation =
          this.installations.find(
            (x) => Number(x.installation_id) === Number(newId)
          ) || {};
      },
      immediate: true,
    },
    dialog(value) {
      if (value) {
        this.loadParameters();
      }
    },
  },

  methods: {
    //===========================================================
    // Date picker methods
    //==========================================================
    addCleaningDateFromPicker(date) {
      if (!date) {
        return;
      }

      if (!Array.isArray(this.form.cleaning_dates)) {
        this.$set(this.form, "cleaning_dates", []);
      }

      if (!this.form.cleaning_dates.includes(date)) {
        this.form.cleaning_dates.push(date);
      }

      this.cleaningDateMenu = false;
    },

    addPruningDateFromPicker(date) {
      if (!date) {
        return;
      }

      if (!Array.isArray(this.form.pruning_dates)) {
        this.$set(this.form, "pruning_dates", []);
      }

      if (!this.form.pruning_dates.includes(date)) {
        this.form.pruning_dates.push(date);
      }

      this.pruningDateMenu = false;
    },

    formatDates(dates) {
      if (!Array.isArray(dates)) {
        return "";
      }

      return dates
        .filter(Boolean)
        .map((date) => this.formatDate(date))
        .join("; ");
    },

    // =============================================================
    // LOAD
    // =============================================================

    async loadParameters() {
      this.loading = true;

      try {
        const response = await api.getHeliocleanParameters(this.installationId);

        this.defaultParams = response.data.default_params || {};

        this.clientParams = response.data.client_params || {};

        this.limits = response.data.limits || {};

        /*
         * IMPORTANT:
         *
         * form = default + client
         *
         * We DO NOT put defaults into clientParams.
         */
        this.form = this.clone({
          ...this.defaultParams,
          ...this.clientParams,
        });

        this.ensureArrays();
      } catch (error) {
        console.error("Unable to load HelioClean parameters", error);

        /*
         * Backend can return an empty structure for a new site.
         * So even if everything is empty, the form remains usable.
         */
        this.defaultParams = {};
        this.clientParams = {};
        this.limits = {};
        this.form = {};

        this.ensureArrays();
      } finally {
        this.loading = false;
      }
    },

    // =============================================================
    // INITIALISE ARRAYS
    // =============================================================

    ensureArrays() {
      if (!Array.isArray(this.form.cleaning_dates)) {
        this.$set(this.form, "cleaning_dates", []);
      }

      if (!Array.isArray(this.form.pruning_dates)) {
        this.$set(this.form, "pruning_dates", []);
      }

      if (!Array.isArray(this.form.temporary_injected_power)) {
        this.$set(this.form, "temporary_injected_power", []);
      }

      // Estimated — 12 months
      if (!Array.isArray(this.form.cleaning_soiling_estimated)) {
        this.$set(
          this.form,
          "cleaning_soiling_estimated",
          Array(12).fill(null)
        );
      }

      if (!Array.isArray(this.form.curtailment_estimated)) {
        this.$set(this.form, "curtailment_estimated", Array(12).fill(null));
      }

      // Retained — 12 months
      if (!Array.isArray(this.form.cleaning_soiling_retained)) {
        this.$set(this.form, "cleaning_soiling_retained", Array(12).fill(null));
      }

      if (!Array.isArray(this.form.curtailment_retained)) {
        this.$set(this.form, "curtailment_retained", Array(12).fill(null));
      }
    },

    // =============================================================
    // LIMITS
    // =============================================================

    hasParameter(key) {
      return Object.prototype.hasOwnProperty.call(this.form, key);
    },

    hasLimit(key) {
      return Array.isArray(this.limits[key]);
    },

    getLimit(key) {
      return this.limits[key] || null;
    },

    getMin(key) {
      const limit = this.getLimit(key);

      return limit ? limit[1] : null;
    },

    getMax(key) {
      const limit = this.getLimit(key);

      return limit ? limit[0] : null;
    },

    hasIncrement(key) {
      const limit = this.getLimit(key);

      return !!(limit && limit.length >= 3 && limit[2] != null);
    },

    getStep(key) {
      const limit = this.getLimit(key);

      return this.hasIncrement(key) ? limit[2] : null;
    },

    getLimitOptions(key) {
      const limit = this.getLimit(key);

      if (!limit || limit.length < 3) {
        return [];
      }

      const max = Number(limit[0]);
      const min = Number(limit[1]);
      const increment = Number(limit[2]);

      if (
        !Number.isFinite(max) ||
        !Number.isFinite(min) ||
        !Number.isFinite(increment) ||
        increment <= 0
      ) {
        return [];
      }

      const values = [];

      for (let value = min; value <= max; value += increment) {
        values.push(value);
      }

      return values;
    },

    getRules(key) {
      const limit = this.getLimit(key);

      if (!limit) {
        return [];
      }

      const min = Number(limit[1]);
      const max = Number(limit[0]);
      const increment = limit.length >= 3 ? Number(limit[2]) : null;

      const rules = [];

      rules.push((value) => {
        if (value === null || value === undefined || value === "") {
          return true;
        }

        const number = Number(value);

        if (!Number.isFinite(number)) {
          return this.$t("_invalid_number_");
        }

        if (number < min || number > max) {
          return `${this.$t("MIN :")} ${min} ${this.$t(
            ",  MAX :"
          )} ${max}`;
        }

        return true;
      });

      if (increment && increment > 0) {
        rules.push((value) => {
          if (value === null || value === undefined || value === "") {
            return true;
          }

          const number = Number(value);

          if (!Number.isFinite(number)) {
            return true;
          }

          const quotient = (number - min) / increment;
          const valid = Math.abs(quotient - Math.round(quotient)) < 0.000001;

          return valid || this.$t("_invalid_increment_");
        });
      }

      return rules;
    },

    // =============================================================
    // TEMPORARY POWER
    // =============================================================

    addTemporaryPower() {
      this.form.temporary_injected_power.push({
        id: Date.now(),
        power: null,
        start: null,
        end: null,
        startMenu: false,
        endMenu: false,
      });
    },

    removeTemporaryPower(index) {
      this.form.temporary_injected_power.splice(index, 1);
    },

    // =============================================================
    // CLEANING DATES
    // =============================================================

    addCleaningDate() {
      this.form.cleaning_dates.push(null);
    },

    removeCleaningDate(index) {
      this.form.cleaning_dates.splice(index, 1);
    },

    updateCleaningDate(index, date) {
      this.$set(this.form.cleaning_dates, index, date);

      this.$set(this.dateMenus.cleaning, index, false);
    },

    // =============================================================
    // PRUNING DATES
    // =============================================================

    addPruningDate() {
      this.form.pruning_dates.push(null);
    },

    removePruningDate(index) {
      this.form.pruning_dates.splice(index, 1);
    },

    updatePruningDate(index, date) {
      this.$set(this.form.pruning_dates, index, date);

      this.$set(this.dateMenus.pruning, index, false);
    },

    // =============================================================
    // MONTHLY VALUES
    // =============================================================

    getMonthlyValues(key) {
      const value = this.effectiveValue(key);

      if (!Array.isArray(value)) {
        return Array(12).fill(null);
      }

      return value;
    },

    getMonthlyValue(key) {
      const value = this.effectiveValue(key);

      if (Array.isArray(value)) {
        return value[0] || null;
      }

      return value;
    },
    getDefaultMonthlyValues(key) {
      const value = this.defaultParams[key];

      return Array.isArray(value) ? value : Array(12).fill(null);
    },

    getDefaultMonthlyValue(key) {
      const value = this.defaultParams[key];

      if (Array.isArray(value)) {
        return value[0] ?? null;
      }

      return value ?? null;
    },

    effectiveValue(key) {
      if (Object.prototype.hasOwnProperty.call(this.clientParams, key)) {
        return this.clientParams[key];
      }

      return this.defaultParams[key];
    },

    // =============================================================
    // DATE FORMAT
    // =============================================================

    formatDate(date) {
      if (!date) {
        return "";
      }

      /*
       * Keep ISO dates for the backend.
       * Only format the displayed value here.
       */
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const parts = date.split("-");

        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }

      return date;
    },

    // =============================================================
    // SAVE
    // =============================================================

    buildClientPayload() {
      const payload = {};

      /*
       * Only send values that differ from defaults.
       *
       * This is important because:
       *
       * default_params = hc-compute
       * client_params  = client overrides
       */

      Object.keys(this.form).forEach((key) => {
        const value = this.form[key];
        const defaultValue = this.defaultParams[key];

        if (!this.areEqual(value, defaultValue)) {
          payload[key] = value;
        }
      });

      return payload;
    },

    areEqual(a, b) {
      return JSON.stringify(a) === JSON.stringify(b);
    },

    async save() {
      const valid = this.$refs.parameterForm.validate();

      if (!valid) {
        return;
      }

      this.saving = true;

      try {
        const clientParams = this.buildClientPayload();

        await api.updateHeliocleanParametersClient(
          this.installationId,
          clientParams
        );

        this.$emit("saved");
        this.close();
      } catch (error) {
        console.error("Unable to save HelioClean parameters", error);
      } finally {
        this.saving = false;
      }
    },

    // =============================================================
    // CLOSE
    // =============================================================

    close() {
      this.dialog = false;
    },

    // =============================================================
    // UTILITY
    // =============================================================

    clone(value) {
      return JSON.parse(JSON.stringify(value));
    },
  },
};
</script>

<style scoped>
.parameter-card {
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0 !important;
}

.section-title {
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 8px;
}

.subsection-title {
  font-weight: 600;
  color: #666;
  margin-bottom: 12px;
}

.temporary-power-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  margin-left: 32px;
}

.temporary-power-field {
  max-width: 140px;
  transform: translateY(12px);
}

.temporary-date-field {
  max-width: 150px;
}

.dates-section {
  margin-left: 0;
}

.default-dates {
  padding: 10px 14px;
  background: #eeeeee;
  border-radius: 5px;
}

.default-date-row {
  display: flex;
  align-items: center;
  color: #777;
  margin-bottom: 4px;
}

.date-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.date-input {
  max-width: 180px;
}

.monthly-section {
  overflow-x: auto;
}

.monthly-header,
.monthly-row {
  display: flex;
  align-items: center;
  min-width: 950px;
}

.row-title {
  width: 80px;
  min-width: 80px;
  font-weight: 600;
  margin-right: 8px;
}

.month-label {
  width: 74px;
  min-width: 74px;
  flex: 0 0 74px;
  margin-right: 0;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
}

.small-field {
  width: 74px;
  min-width: 74px;
  max-width: 74px;
  flex: 0 0 74px;
  margin-right: 0;
}

.small-field-pruning {
  width: 120px;
  min-width: 120px;
}

.month-label-pruning {
  width: 120px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
}

::v-deep .v-text-field {
  margin-top: 0;
  padding-top: 0;
}

::v-deep .v-input__control {
  min-height: 32px !important;
}

::v-deep .v-text-field input {
  font-size: 13px;
}

::v-deep .v-input--is-disabled {
  opacity: 0.7;
}

.default-value {
  color: #777;
  font-size: 13px;
}

.date-title {
  font-weight: 600;
}

.date-box-wrapper {
  width: 100%;
}

.date-box-label {
  color: #777;
  font-size: 13px;
  margin-bottom: 4px;
}

.default-date-box {
  background: #eeeeee;
}

.client-date-box {
  background: white;
}

::v-deep .client-date-box .v-chip {
  margin: 3px 4px 3px 0;
}

::v-deep .default-date-box textarea {
  color: #777 !important;
}

.temporary-power-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  margin-left: 32px;
}

.temporary-power-field,
.temporary-date-field {
  margin-top: 0 !important;
  padding-top: 0 !important;
}
</style>
